/*
 * Cart drawer — Chaandbali
 *
 * Dawn re-renders the whole drawer (innerHTML) on every cart change, and on
 * an add from the product page it does so twice (renderContents, then the
 * cartUpdate subscriber). Every photo reloads and the list jumps: that is
 * the flicker. This file takes over the drawer's update path and patches
 * the DOM in place instead:
 *
 *   - lines are matched by line-item key; only the parts that changed are
 *     swapped, so images never reload
 *   - removed lines fold away, added lines grow in, the subtotal rolls
 *   - quantity/remove requests use the line key (not the line index) and
 *     run one at a time, so fast taps can't hit the wrong row
 *   - a row being updated stays interactive; only its price dims
 *
 * Also defines:
 *   <cart-upsell>    "Complete the look" rail with one-tap add
 *   <cart-total>     subtotal count-up/down
 *   <cart-progress>  free-delivery bar animation
 * plus the drawer's entrance choreography, mobile drag-to-dismiss, and the
 * "Added" confirmation on product-page Add to cart buttons.
 *
 * If this file fails to load, Dawn's own cart.js / cart-drawer.js still
 * handle everything.
 */
(() => {
  if (customElements.get('cart-upsell')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isMobile = window.matchMedia('(max-width: 749px)');
  const SEED_KEY = 'cb-upsell-seed';
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const MOVE_MS = 450;
  const state = { total: null, progress: null };
  const pending = {};
  let queue = Promise.resolve();
  let removalEndsAt = 0;

  const PLUS_SVG =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"><path d="M8 3v10M3 8h10"/></svg>';
  const ARROW_SVG =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>';
  const CHECK_SVG =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 8.5l3 3 7-7"/></svg>';

  /* ------------------------------------------------------------------
   * Utilities
   * ------------------------------------------------------------------ */
  const store = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (e) {}
    },
  };

  const drawerEl = () => document.querySelector('cart-drawer');
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, reduceMotion.matches ? 0 : ms));
  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const moneyFormatter = () => {
    const drawer = drawerEl();
    const currency = (drawer && drawer.dataset.currency) || 'AUD';
    const locale = (drawer && drawer.dataset.locale) || document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
    } catch (e) {
      return new Intl.NumberFormat('en', { style: 'currency', currency });
    }
  };

  const formatMoney = (cents) => moneyFormatter().format(cents / 100);

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const sizedImage = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    let data = {};
    try {
      data = await response.json();
    } catch (e) {}
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || (window.cartStrings && window.cartStrings.error) || 'Error');
      error.data = data;
      throw error;
    }
    return data;
  };

  const announce = (message) => {
    const region = document.getElementById('CartDrawer-LiveRegionText');
    if (region) region.textContent = message;
  };

  const publishCartUpdate = (data, variantId) => {
    // source 'cart-items' tells Dawn's own cart subscribers not to re-fetch.
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: data, variantId });
    }
  };

  /* ------------------------------------------------------------------
   * Row motion
   * ------------------------------------------------------------------ */
  const collapseRow = (row) => {
    if (!row || row.classList.contains('is-removing')) return;
    if (reduceMotion.matches) {
      row.classList.add('is-removing');
      return;
    }
    row.style.height = `${row.getBoundingClientRect().height}px`;
    void row.offsetHeight;
    row.classList.add('is-removing');
    removalEndsAt = Math.max(removalEndsAt, performance.now() + MOVE_MS);
  };

  const restoreRow = (row) => {
    if (!row) return;
    row.classList.remove('is-removing');
    row.style.height = '';
  };

  const growRow = (row) => {
    if (reduceMotion.matches) return;
    const height = row.getBoundingClientRect().height;
    row.classList.add('is-growing', 'is-new');
    row.style.height = '0px';
    row.style.paddingTop = '0px';
    row.style.paddingBottom = '0px';
    void row.offsetHeight;
    row.style.height = `${height}px`;
    row.style.paddingTop = '';
    row.style.paddingBottom = '';
    setTimeout(() => {
      row.classList.remove('is-growing');
      row.style.height = '';
    }, MOVE_MS + 20);
    row.addEventListener('animationend', () => row.classList.remove('is-new'), { once: true });
  };

  // Header bag count pops when it changes (not on page load).
  const bumpBubble = () => {
    if (reduceMotion.matches) return;
    const count = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!count) return;
    count.classList.remove('cb-bump');
    void count.offsetWidth;
    count.classList.add('cb-bump');
  };

  /* ------------------------------------------------------------------
   * In-place drawer update
   * ------------------------------------------------------------------ */
  const syncRow = (row, next) => {
    ['id', 'style', 'data-product-id'].forEach((name) => {
      const value = next.getAttribute(name);
      if (value === null) row.removeAttribute(name);
      else if (name === 'style') row.style.setProperty('--i', next.style.getPropertyValue('--i'));
      else row.setAttribute(name, value);
    });

    const key = row.dataset.key;
    const media = row.querySelector('.cart-item__media img');
    const nextMedia = next.querySelector('.cart-item__media img');
    if (media && nextMedia && media.getAttribute('src') !== nextMedia.getAttribute('src')) {
      row.querySelector('.cart-item__media').replaceWith(document.importNode(next.querySelector('.cart-item__media'), true));
    }

    ['.cart-item__details', '.cart-item__totals', '.cart-item__quantity'].forEach((selector) => {
      const current = row.querySelector(selector);
      const incoming = next.querySelector(selector);
      if (!current || !incoming) return;
      // A newer change for this line is still in flight: keep the stepper
      // the shopper is pressing instead of snapping it back.
      if (selector === '.cart-item__quantity' && pending[key] > 0) return;
      if (current.innerHTML !== incoming.innerHTML) current.replaceWith(document.importNode(incoming, true));
    });
  };

  const syncLines = (container, nextContainer) => {
    const current = new Map();
    container.querySelectorAll(':scope > .cart-item').forEach((row) => current.set(row.dataset.key, row));
    const keep = new Set();
    let ref = container.firstElementChild;

    [...nextContainer.children].forEach((next) => {
      let row = current.get(next.dataset.key);
      let isNew = false;
      if (row) {
        // Still folding because a later remove is queued: leave it folding.
        if (!pending[row.dataset.key]) restoreRow(row);
        syncRow(row, next);
      } else {
        row = document.importNode(next, true);
        isNew = true;
      }
      keep.add(row);
      while (ref && ref !== row && !keep.has(ref) && ref.classList.contains('is-removing')) ref = ref.nextElementSibling;
      if (row === ref) {
        ref = ref.nextElementSibling;
      } else {
        container.insertBefore(row, ref);
      }
      if (isNew) growRow(row);
    });

    current.forEach((row) => {
      if (keep.has(row)) return;
      collapseRow(row);
      setTimeout(() => row.remove(), reduceMotion.matches ? 0 : MOVE_MS);
    });
  };

  const replaceIfChanged = (current, next, compare) => {
    if (!current || !next) return;
    if (compare(current) !== compare(next)) current.replaceWith(document.importNode(next, true));
  };

  const swapInner = async (drawer, inner, nextInner, nextEmpty) => {
    const apply = () => {
      inner.innerHTML = nextInner.innerHTML;
      drawer.classList.toggle('is-empty', nextEmpty);
      state.progress = null;
    };
    if (!drawer.classList.contains('active') || reduceMotion.matches) {
      apply();
      return;
    }
    // Let a folding row finish, cross-fade, then replay the entrance.
    await wait(Math.max(0, removalEndsAt - performance.now()));
    drawer.classList.add('is-swapping');
    await wait(180);
    apply();
    void drawer.offsetHeight;
    drawer.classList.remove('is-swapping');
    if (drawer._cbMarkEntering) drawer._cbMarkEntering();
  };

  const applyDrawerHTML = async (html) => {
    const drawer = drawerEl();
    if (!drawer || !html) return;
    const doc = parseHTML(html);
    const nextInner = doc.querySelector('.drawer__inner');
    const inner = drawer.querySelector('.drawer__inner');
    if (!nextInner || !inner) return;

    const nextEmpty = !!nextInner.querySelector('.drawer__inner-empty');
    const currentEmpty = !!inner.querySelector('.drawer__inner-empty');
    if (nextEmpty !== currentEmpty || !drawer.classList.contains('active')) {
      await swapInner(drawer, inner, nextInner, nextEmpty);
      return;
    }
    if (nextEmpty) return;

    drawer.classList.remove('is-empty');

    const heading = inner.querySelector('.drawer__heading');
    const nextHeading = nextInner.querySelector('.drawer__heading');
    if (heading && nextHeading && heading.innerHTML !== nextHeading.innerHTML) heading.innerHTML = nextHeading.innerHTML;

    replaceIfChanged(
      inner.querySelector('cart-progress'),
      nextInner.querySelector('cart-progress'),
      (el) => `${el.dataset.progress}|${el.className}|${el.textContent.trim()}`
    );

    const lines = inner.querySelector('.cb-lines');
    const nextLines = nextInner.querySelector('.cb-lines');
    if (lines && nextLines) syncLines(lines, nextLines);

    const upsell = inner.querySelector('cart-upsell');
    const nextUpsell = nextInner.querySelector('cart-upsell');
    if (upsell && nextUpsell) {
      upsell.dataset.cartIds = nextUpsell.dataset.cartIds || '';
      if (typeof upsell.syncCart === 'function') upsell.syncCart();
    }

    replaceIfChanged(
      inner.querySelector('.cart-drawer__footer'),
      nextInner.querySelector('.cart-drawer__footer'),
      (el) => el.textContent.replace(/\s+/g, ' ').trim()
    );

    const checkout = inner.querySelector('#CartDrawer-Checkout');
    const nextCheckout = nextInner.querySelector('#CartDrawer-Checkout');
    if (checkout && nextCheckout) checkout.disabled = nextCheckout.disabled;

    announce(window.cartStrings && window.cartStrings.updated ? window.cartStrings.updated : 'Cart updated');
  };

  const applyState = async (data) => {
    if (!data || !data.sections) return;
    const bubble = data.sections['cart-icon-bubble'];
    const bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubble && bubbleEl) {
      const source = parseHTML(bubble).querySelector('.shopify-section');
      if (source) {
        bubbleEl.innerHTML = source.innerHTML;
        bumpBubble();
      }
    }
    await applyDrawerHTML(data.sections['cart-drawer']);
  };

  /* ------------------------------------------------------------------
   * Cart actions
   * ------------------------------------------------------------------ */
  const changeLine = (row, quantity, variantId) => {
    const key = row.dataset.key;
    pending[key] = (pending[key] || 0) + 1;
    row.classList.add('is-updating');
    const errorText = row.querySelector('.cart-item__error-text');
    if (errorText) errorText.textContent = '';
    if (quantity === 0) collapseRow(row);

    return enqueue(async () => {
      try {
        const data = await postJSON(`${window.routes.cart_change_url}.js`, {
          id: key,
          quantity,
          sections: SECTIONS,
          sections_url: window.location.pathname,
        });
        pending[key] -= 1;
        await applyState(data);
        publishCartUpdate(data, variantId);
      } catch (error) {
        pending[key] -= 1;
        restoreRow(row);
        const input = row.querySelector('.quantity__input');
        if (input) input.value = input.getAttribute('value');
        const text = row.querySelector('.cart-item__error-text');
        if (text) text.textContent = error.message;
      } finally {
        if (!pending[key]) row.classList.remove('is-updating');
      }
    });
  };

  const addVariant = (variantId, button) => {
    const label = button.querySelector('.cb-upsell__add-label');
    const original = label ? label.innerHTML : '';
    button.classList.remove('is-error');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    return enqueue(async () => {
      try {
        const data = await postJSON(`${window.routes.cart_add_url}.js`, {
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: SECTIONS,
          sections_url: window.location.pathname,
        });
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.removeAttribute('aria-busy');
        if (label) label.innerHTML = CHECK_SVG;
        button.setAttribute('aria-label', 'Added to cart');
        await wait(420);
        await applyState(data);
        publishCartUpdate(data, variantId);

        // New lines land at the top of the list: bring it into view.
        const scroller = document.querySelector('cart-drawer cart-drawer-items');
        if (scroller && scroller.scrollTop > 0) {
          scroller.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
      } catch (error) {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        void button.offsetWidth;
        button.classList.add('is-error');
        const originalAria = button.getAttribute('aria-label');
        button.setAttribute('aria-label', error.message || 'Unavailable');
        button.title = error.message || 'Unavailable';
        setTimeout(() => {
          if (!button.isConnected) return;
          button.setAttribute('aria-label', originalAria);
          button.removeAttribute('title');
          if (label) label.innerHTML = original;
        }, 2400);
      }
    });
  };

  /* ------------------------------------------------------------------
   * <cart-total>: count from the previous subtotal to the new one.
   * ------------------------------------------------------------------ */
  class CartTotal extends HTMLElement {
    connectedCallback() {
      const to = parseInt(this.dataset.cents, 10);
      const from = state.total;
      state.total = to;
      if (from === null || from === to || Number.isNaN(to) || reduceMotion.matches) return;

      const finalText = this.textContent;
      const suffix = (finalText.match(/\s[A-Z]{3}\s*$/) || [''])[0];
      const formatter = moneyFormatter();
      const start = performance.now();
      const duration = 520;
      const ease = (t) => 1 - Math.pow(1 - t, 3);

      const tick = (now) => {
        if (!this.isConnected) return;
        const t = Math.min(1, (now - start) / duration);
        if (t < 1) {
          this.textContent = formatter.format((from + (to - from) * ease(t)) / 100) + suffix;
          requestAnimationFrame(tick);
        } else {
          this.textContent = finalText;
        }
      };
      requestAnimationFrame(tick);
    }
  }
  customElements.define('cart-total', CartTotal);

  /* ------------------------------------------------------------------
   * <cart-progress>: grow the bar from where it was.
   * ------------------------------------------------------------------ */
  class CartProgress extends HTMLElement {
    connectedCallback() {
      const fill = this.querySelector('.cb-progress__fill');
      const to = Math.max(0, Math.min(100, parseFloat(this.dataset.progress) || 0));
      const from = state.progress;
      state.progress = to;
      if (!fill || reduceMotion.matches) return;
      // First paint already has the right width inline; only animate changes.
      if (from === null || from === to) return;

      fill.style.transition = 'none';
      fill.style.transform = `scaleX(${from / 100})`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fill.style.transition = '';
          fill.style.transform = `scaleX(${to / 100})`;
        });
      });
    }
  }
  customElements.define('cart-progress', CartProgress);

  /* ------------------------------------------------------------------
   * <cart-upsell>: recommendations rail with one-tap add.
   * ------------------------------------------------------------------ */
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.cb-upsell__track');
      this.section = this.closest('.cb-upsell');
      if (!this.track) return;

      this.addEventListener('click', this.onClick);
      this.track.addEventListener('scroll', this.updateArrows, { passive: true });
      if (this.section) {
        this.section.querySelectorAll('.cb-upsell__arrow').forEach((btn) => btn.addEventListener('click', this.onArrow));
      }

      this.loadRecommendations();
      this.updateArrows();
      if (!this.track.children.length && !this.dataset.url && this.section) this.section.hidden = true;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    get cartIds() {
      return (this.dataset.cartIds || '').split(',').filter(Boolean);
    }

    /* Keep the seed product stable for the session so the rail doesn't
     * reshuffle every time the shopper adds something from it. */
    get seed() {
      const ids = this.cartIds;
      const saved = store.get(SEED_KEY);
      if (saved && ids.includes(saved)) return saved;
      const seed = this.dataset.seed;
      if (seed) store.set(SEED_KEY, seed);
      return seed;
    }

    async loadRecommendations() {
      const url = this.dataset.url;
      const seed = this.seed;
      if (!url || !seed) return;

      const cacheKey = `cb-upsell:${seed}`;
      let products = null;
      const cached = store.get(cacheKey);
      if (cached) {
        try {
          products = JSON.parse(cached);
        } catch (e) {}
      }

      if (products) {
        this.render(products, false);
        return;
      }

      try {
        for (const intent of ['complementary', 'related']) {
          const response = await fetch(`${url}.json?product_id=${seed}&limit=10&intent=${intent}`);
          if (!response.ok) continue;
          const data = await response.json();
          if (data.products && data.products.length >= 2) {
            products = data.products.map((p) => ({
              id: p.id,
              title: p.title,
              url: p.url,
              image: p.featured_image,
              price: p.price,
              price_varies: p.price_varies,
              available: p.available,
              variant_id: p.variants && p.variants.length === 1 && p.variants[0].available ? p.variants[0].id : null,
            }));
            break;
          }
        }
      } catch (e) {
        return;
      }

      if (!products || !this.isConnected) return;
      store.set(cacheKey, JSON.stringify(products));
      this.render(products, true);
    }

    desired() {
      const inCart = new Set(this.cartIds.map(String));
      return (this.products || []).filter((p) => p.available && !inCart.has(String(p.id))).slice(0, 8);
    }

    render(products, animate) {
      this.products = products;
      const items = this.desired();
      if (items.length < 2) return;

      this.track.innerHTML = items.map((p) => this.cardHTML(p)).join('');
      this.track.scrollLeft = 0;
      if (this.section) this.section.hidden = false;
      if (animate && !reduceMotion.matches) {
        this.track.classList.remove('is-swapping');
        void this.track.offsetWidth;
        this.track.classList.add('is-swapping');
      }
      this.updateArrows();
    }

    /* Called after every in-place cart update: cards for products now in
     * the cart fold out of the rail; ones taken out of the cart come back. */
    syncCart() {
      if (!this.track) return;
      const live = () => [...this.track.children].filter((c) => !c.classList.contains('is-leaving'));
      const inCart = new Set(this.cartIds.map(String));
      const wanted = this.products ? this.desired() : null;
      const wantedIds = wanted ? new Set(wanted.map((p) => String(p.id))) : null;

      live().forEach((card) => {
        const id = card.dataset.productId;
        const keep = wantedIds ? wantedIds.has(id) : !inCart.has(id);
        if (!keep) this.leave(card);
      });

      if (wanted) {
        wanted.forEach((p, index) => {
          if (this.track.querySelector(`[data-product-id="${p.id}"]:not(.is-leaving)`)) return;
          const holder = document.createElement('ul');
          holder.innerHTML = this.cardHTML(p);
          const card = holder.firstElementChild;
          card.classList.add('is-entering');
          this.track.insertBefore(card, live()[index] || null);
        });
      }

      if (this.section && live().length === 0) {
        setTimeout(() => {
          if (live().length === 0) this.section.hidden = true;
        }, MOVE_MS);
      }
      setTimeout(this.updateArrows, MOVE_MS);
    }

    leave(card) {
      if (reduceMotion.matches) {
        card.remove();
        return;
      }
      card.classList.add('is-leaving');
      setTimeout(() => card.remove(), MOVE_MS + 20);
    }

    cardHTML(p) {
      const title = escapeHtml(p.title);
      const url = escapeHtml(p.url);
      const price = `${p.price_varies ? 'From ' : ''}${formatMoney(p.price)}`;
      const image = p.image
        ? `<img src="${escapeHtml(sizedImage(p.image, 160))}" alt="" loading="lazy" width="80" height="100">`
        : '';
      const action = p.variant_id
        ? `<button type="button" class="cb-upsell__add" data-variant-id="${p.variant_id}" aria-label="Add ${title} to cart"><span class="cb-upsell__add-label">${PLUS_SVG}</span></button>`
        : `<a class="cb-upsell__add" href="${url}" aria-label="Choose options for ${title}"><span class="cb-upsell__add-label">${ARROW_SVG}</span></a>`;
      return `<li class="cb-upsell__card" data-product-id="${p.id}">
        <a class="cb-upsell__media" href="${url}" tabindex="-1" aria-hidden="true">${image}</a>
        <span class="cb-upsell__info">
          <a class="cb-upsell__title" href="${url}">${title}</a>
          <span class="cb-upsell__price">${price}</span>
        </span>
        ${action}
      </li>`;
    }

    onArrow = (event) => {
      const dir = parseInt(event.currentTarget.dataset.dir, 10) || 1;
      const card = this.track.querySelector('.cb-upsell__card');
      const step = card ? card.getBoundingClientRect().width + 10 : 140;
      this.track.scrollBy({ left: dir * step * 2, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    updateArrows = () => {
      if (!this.section || !this.track) return;
      const [prev, next] = this.section.querySelectorAll('.cb-upsell__arrow');
      if (!prev || !next) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      prev.disabled = this.track.scrollLeft <= 2;
      next.disabled = this.track.scrollLeft >= max;
    };

    onClick = (event) => {
      const button = event.target.closest('button.cb-upsell__add');
      if (!button) return;
      event.preventDefault();
      if (button.classList.contains('is-loading') || button.classList.contains('is-added')) return;
      addVariant(button.dataset.variantId, button);
    };
  }
  customElements.define('cart-upsell', CartUpsell);

  /* ------------------------------------------------------------------
   * Take over Dawn's drawer update path.
   * ------------------------------------------------------------------ */
  customElements.whenDefined('cart-drawer-items').then(() => {
    const proto = customElements.get('cart-drawer-items').prototype;
    const originalUpdate = proto.updateQuantity;

    proto.updateQuantity = function (line, quantity, event, name, variantId) {
      const origin = event && (event.target || event.currentTarget);
      const row =
        (origin && origin.closest && origin.closest('.cart-item')) ||
        (event && event.currentTarget && event.currentTarget.closest && event.currentTarget.closest('.cart-item')) ||
        document.getElementById(`CartDrawer-Item-${line}`);
      if (!row || !row.dataset.key) return originalUpdate.call(this, line, quantity, event, name, variantId);
      return changeLine(row, Number(quantity), variantId);
    };

    proto.onCartUpdate = function () {
      return fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((html) => applyDrawerHTML(html))
        .catch((e) => console.error(e));
    };
  });

  customElements.whenDefined('cart-drawer').then(() => {
    const proto = customElements.get('cart-drawer').prototype;
    const originalRender = proto.renderContents;
    proto.renderContents = function (parsedState) {
      this.classList.remove('is-empty');
      if (this.classList.contains('active') && parsedState && parsedState.sections && parsedState.sections['cart-drawer']) {
        applyState(parsedState);
        return;
      }
      const result = originalRender.call(this, parsedState);
      bumpBubble();
      return result;
    };
  });

  /* ------------------------------------------------------------------
   * Product page / quick-add "Added" confirmation.
   * ------------------------------------------------------------------ */
  const confirmAdded = (button) => {
    if (button.querySelector('.cb-added-label')) return;
    const label = document.createElement('span');
    label.className = 'cb-added-label';
    label.innerHTML = `${CHECK_SVG}Added`;
    button.appendChild(label);
    button.classList.add('cb-added');
    setTimeout(() => {
      button.classList.remove('cb-added');
      label.remove();
    }, 1600);
  };

  const watchProductForms = () => {
    if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'product-form') return;
      document.querySelectorAll('.product-form__submit.loading').forEach(confirmAdded);
    });
  };

  /* ------------------------------------------------------------------
   * Drawer-level motion and gestures.
   * ------------------------------------------------------------------ */
  const enhanceDrawer = (drawer) => {
    if (!drawer || drawer.dataset.cbEnhanced) return;
    drawer.dataset.cbEnhanced = 'true';

    let enteringTimer;
    const markEntering = () => {
      if (reduceMotion.matches) return;
      drawer.setAttribute('data-entering', '');
      clearTimeout(enteringTimer);
      enteringTimer = setTimeout(() => drawer.removeAttribute('data-entering'), 1400);
    };
    drawer._cbMarkEntering = markEntering;

    // Open: stagger the contents in. Dawn toggles `active` on the element.
    new MutationObserver((records) => {
      for (const record of records) {
        const was = (record.oldValue || '').split(/\s+/).includes('active');
        const is = drawer.classList.contains('active');
        if (is && !was) markEntering();
        if (!is && was) drawer.removeAttribute('data-entering');
        document.documentElement.classList.toggle('cb-cart-open', is);
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });

    // Mobile sheet: drag the handle/header down to dismiss.
    let drag = null;

    const onTouchStart = (event) => {
      if (!isMobile.matches || !drawer.classList.contains('active')) return;
      if (!event.target.closest('.cb-sheet-handle, .drawer__header')) return;
      if (event.target.closest('button, a')) return;
      const inner = drawer.querySelector('.drawer__inner');
      const overlay = drawer.querySelector('.cart-drawer__overlay');
      if (!inner) return;
      const touch = event.touches[0];
      drag = { inner, overlay, startY: touch.clientY, dy: 0, lastY: touch.clientY, lastT: performance.now(), v: 0 };
      inner.style.transition = 'none';
      if (overlay) overlay.style.transition = 'none';
    };

    const onTouchMove = (event) => {
      if (!drag) return;
      const touch = event.touches[0];
      const now = performance.now();
      const raw = touch.clientY - drag.startY;
      // Resist upward pulls like a rubber band; follow downward pulls 1:1.
      drag.dy = raw < 0 ? -Math.sqrt(-raw) * 2 : raw;
      drag.v = (touch.clientY - drag.lastY) / Math.max(1, now - drag.lastT);
      drag.lastY = touch.clientY;
      drag.lastT = now;
      drag.inner.style.transform = `translateY(${drag.dy}px)`;
      if (drag.overlay) {
        const height = drag.inner.getBoundingClientRect().height || 1;
        drag.overlay.style.opacity = String(Math.max(0, 1 - Math.max(0, drag.dy) / height));
      }
    };

    const onTouchEnd = () => {
      if (!drag) return;
      const { inner, overlay, dy, v } = drag;
      drag = null;
      const height = inner.getBoundingClientRect().height || 1;
      const dismiss = dy > height * 0.25 || (v > 0.5 && dy > 24);

      inner.style.transition = dismiss ? '' : 'transform 0.5s cubic-bezier(0.34, 1.36, 0.64, 1)';
      inner.style.transform = '';
      if (overlay) {
        overlay.style.transition = '';
        overlay.style.opacity = '';
      }
      if (dismiss) {
        drawer.close();
      } else {
        inner.addEventListener('transitionend', () => (inner.style.transition = ''), { once: true });
      }
    };

    drawer.addEventListener('touchstart', onTouchStart, { passive: true });
    drawer.addEventListener('touchmove', onTouchMove, { passive: true });
    drawer.addEventListener('touchend', onTouchEnd);
    drawer.addEventListener('touchcancel', onTouchEnd);
  };

  const init = () => {
    enhanceDrawer(drawerEl());
    watchProductForms();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
